import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Send, Search, FileText } from "lucide-react";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "src/@/components/ui/dialog";
import { Input } from "src/@/components/ui/input";
import { Button } from "src/@/components/ui/button";

import v1_actionMessengerTemplates from "src/actions/v1/meta/messenger/v1_actionMessengerTemplates";
import v1_actionMessengerEnviarTemplateChat from "src/actions/v1/meta/messenger/v1_actionMessengerEnviarTemplateChat";
import MessengerPreview from "src/sections/@dashboard/configuracoes/templates-messenger/MessengerPreview";
import {
    variaveisDoTemplate, textoDoBody, botoesDoTemplate, componentePorTipo
} from "src/sections/@dashboard/configuracoes/templates-messenger/templatesMessenger.utils";

const BRAND_COLOR = "#7C3AED";
const BRAND_BG_SUBTLE = "bg-[#7C3AED]/5";
const BRAND_BORDER = "border-[#7C3AED]";
const BRAND_TEXT = "text-[#7C3AED]";

// Título e corpo têm numeração independente na Meta: no formato posicional o {{1}} do
// título NÃO é o mesmo parâmetro que o {{1}} do corpo. Sem prefixo os dois campos
// compartilhariam o mesmo valor no state e o usuário não conseguiria preenchê-los
// separadamente.
const chaveHeader = (ph) => `h:${ph}`;
const chaveBody = (ph) => `b:${ph}`;

const mapaPorPlaceholder = (lista, valores, prefixar) => Object.fromEntries(
    (lista || []).map((ph) => [ph, valores[prefixar(ph)]])
);

/**
 * Envio de utility message (template) pelo chat do Messenger.
 *
 * Espelha ModalTemplatesWhatsApp, com as diferenças do canal:
 *  - só lista templates APPROVED (a Meta recusa qualquer outro status no envio);
 *  - o destinatário é o PSID (chat.messengerUser.id), não um telefone;
 *  - suporta variáveis NOMEADAS além de posicionais, então os parâmetros vão nomeados
 *    por componente em vez de um array plano de `positions`;
 *  - o canal é resolvido por `sessao` no backend.
 *
 * A mensagem enviada aparece no chat pelo webhook `message_echoes` da Meta — não há
 * inserção otimista aqui, pelo mesmo motivo do envio de texto deste canal.
 */
export default function ModalTemplatesMessenger({ dialogTemplates, onClose, chat, onEnviado }) {
    const { t } = useTranslation();

    const [templates, setTemplates] = useState([]);
    const [loadingTemplates, setLoadingTemplates] = useState(false);
    const [pesquisa, setPesquisa] = useState("");
    const [selecionado, setSelecionado] = useState(null);
    const [valores, setValores] = useState({});
    const [valoresBotoes, setValoresBotoes] = useState({});
    const [enviando, setEnviando] = useState(false);
    const [erroEnvio, setErroEnvio] = useState("");

    const psid = chat?.messengerUser?.id;

    // Sem `chat?.sessao` no guard: esse campo vem de um lookup que casa pelo sessao PADRAO
    // do cliente (herdado de quando so existia 1 canal por cliente) e nunca bate com o
    // sessao proprio da Pagina do Messenger — chega undefined aqui. O backend resolve o
    // canal mesmo sem sessao (assume a unica Pagina Messenger do cliente).
    const buscarTemplates = async () => {
        if (!chat?.cliente_id) return;
        setLoadingTemplates(true);
        setErroEnvio("");
        try {
            const res = await v1_actionMessengerTemplates(chat.cliente_id, {
                sessao: chat.sessao,
                status: 'APPROVED'
            });
            setTemplates(res.data?.data?.templates || []);
        } catch (error) {
            setTemplates([]);
            setErroEnvio(error.response?.data?.message || t('chat.templates.errors.tryAgain'));
        } finally {
            setLoadingTemplates(false);
        }
    };

    useEffect(() => {
        if (!dialogTemplates) return;
        setSelecionado(null);
        setValores({});
        setValoresBotoes({});
        setErroEnvio("");
        buscarTemplates();
    }, [dialogTemplates]); // eslint-disable-line react-hooks/exhaustive-deps

    const vars = useMemo(() => (selecionado ? variaveisDoTemplate(selecionado) : null), [selecionado]);

    const header = componentePorTipo(selecionado?.components, 'HEADER');
    const headerFormato = String(header?.format || '').toUpperCase();

    const faltaPreencher = useMemo(() => {
        if (!vars) return false;
        const textos = [...vars.header.map(chaveHeader), ...vars.body.map(chaveBody)]
            .some((chave) => !String(valores[chave] || '').trim());
        const botoes = vars.buttons.some((b) => !String(valoresBotoes[b.index] || '').trim());
        return textos || botoes;
    }, [vars, valores, valoresBotoes]);

    const semVariaveis = !!selecionado
        && !vars?.header?.length && !vars?.body?.length && !vars?.buttons?.length;

    const handleSelecionar = (tpl) => {
        setSelecionado(tpl);
        setValores({});
        setValoresBotoes({});
        setErroEnvio("");
    };

    const handleEnviar = async () => {
        if (!selecionado) return;

        if (!psid) {
            setErroEnvio(t('chat.templates.messenger.noPsid', 'Esta conversa não tem um destinatário do Messenger identificado.'));
            return;
        }

        setEnviando(true);
        setErroEnvio("");
        try {
            await v1_actionMessengerEnviarTemplateChat(chat.cliente_id, {
                sessao: chat.sessao,
                templateId: selecionado._id,
                psid,
                parametros: {
                    header: (vars?.header || []).map((ph) => ({
                        ...(vars.isNamed ? { param_name: ph } : {}),
                        text: valores[chaveHeader(ph)]
                    })),
                    body: (vars?.body || []).map((ph) => ({
                        ...(vars.isNamed ? { param_name: ph } : {}),
                        text: valores[chaveBody(ph)]
                    })),
                    buttons: (vars?.buttons || []).map((b) => ({
                        type: b.type,
                        value: valoresBotoes[b.index] || ''
                    }))
                }
            });

            onEnviado?.();
            onClose();
        } catch (error) {
            setErroEnvio(
                error.response?.data?.message
                || t('chat.templates.errors.tryAgain')
            );
        } finally {
            setEnviando(false);
        }
    };

    const listaFiltrada = (templates || []).filter((tpl) => {
        const termo = pesquisa.toLowerCase();
        if (!termo) return true;
        return (tpl?.templateName || '').toLowerCase().includes(termo)
            || (tpl?.name || '').toLowerCase().includes(termo)
            || textoDoBody(tpl?.components).toLowerCase().includes(termo);
    });

    const campoVariavel = (chave, rotulo, valor, onChange, marcador) => (
        <div key={chave}>
            <label className="text-xs text-gray-500 mb-1 flex items-center gap-1.5">
                {marcador && (
                    <span className="bg-violet-100 text-violet-700 font-mono text-[10px] rounded px-1.5 py-0.5">
                        {marcador}
                    </span>
                )}
                {rotulo}
            </label>
            <input
                type="text"
                value={valor}
                onChange={(e) => onChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-1 focus:ring-[#7C3AED] focus:border-[#7C3AED] transition-all"
            />
        </div>
    );

    return (
        <Dialog open={dialogTemplates} onOpenChange={onClose}>
            <DialogContent className="max-w-7xl w-[90vw] h-[85vh] flex flex-col p-0 gap-0 border-0 rounded-2xl overflow-hidden shadow-2xl">
                <DialogHeader className="flex-shrink-0 px-6 py-4 border-b border-gray-100 bg-white/80 dark:bg-none backdrop-blur-sm sticky top-0 z-10">
                    <DialogTitle className="text-lg font-semibold flex items-center gap-2.5 text-gray-800 justify-between">
                        <div className="flex items-center gap-2 flex-row">
                            <div className={`p-1.5 rounded-lg ${BRAND_BG_SUBTLE} ${BRAND_TEXT}`}>
                                <FileText className="w-5 h-5" />
                            </div>
                            {t('chat.templates.messenger.title', 'Templates do Messenger')}
                        </div>
                        <Button variant="outline" onClick={onClose}>
                            {t('common.buttons.close')}
                        </Button>
                    </DialogTitle>
                </DialogHeader>

                <div className="flex-1 flex min-h-0 bg-white">
                    {/* Lista */}
                    <div className="w-[400px] flex flex-col border-r border-gray-100 bg-gray-50/50">
                        <div className="flex-shrink-0 p-4 pb-2">
                            <div className="relative">
                                <Input
                                    value={pesquisa}
                                    onChange={(e) => setPesquisa(e.target.value)}
                                    placeholder={t('chat.templates.searchByNameOrContent')}
                                    className="w-full pl-10 pr-4 py-2 text-sm bg-white border-gray-200 rounded-xl focus:ring-[#7C3AED] focus:border-[#7C3AED] shadow-sm"
                                />
                                <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar">
                            {loadingTemplates ? (
                                <div className="flex flex-col items-center justify-center h-40 gap-3">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#7C3AED]" />
                                    <p className="text-sm text-gray-500">{t('chat.templates.loading')}</p>
                                </div>
                            ) : listaFiltrada.length === 0 ? (
                                <div className="flex flex-col items-center justify-center h-40 text-center p-4">
                                    <div className="text-4xl mb-3 grayscale opacity-30">📭</div>
                                    <p className="text-sm text-gray-500">
                                        {pesquisa
                                            ? t('chat.templates.noSearchResults')
                                            : t('chat.templates.messenger.emptyApproved', 'Nenhum template aprovado nesta Página. Crie em Configurações > Templates.')}
                                    </p>
                                </div>
                            ) : (
                                listaFiltrada.map((tpl) => {
                                    const isSelected = selecionado?._id === tpl?._id;
                                    return (
                                        <div
                                            key={tpl._id}
                                            onClick={() => handleSelecionar(tpl)}
                                            className={`group relative p-3.5 cursor-pointer border transition-all duration-200 rounded-xl ${isSelected
                                                ? `${BRAND_BG_SUBTLE} ${BRAND_BORDER} ring-1 ring-[#7C3AED]/20 shadow-sm`
                                                : 'bg-white border-gray-200 hover:border-gray-300'
                                                }`}
                                        >
                                            <div className="flex justify-between items-start mb-1.5">
                                                <h3 className={`font-medium text-sm truncate pr-2 ${isSelected ? 'text-[#7C3AED]' : 'text-gray-900'}`}>
                                                    {tpl?.templateName || tpl?.name}
                                                </h3>
                                                {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-[#7C3AED]" />}
                                            </div>

                                            <div className="flex items-center gap-2 mb-2">
                                                <span className="text-[10px] uppercase font-bold tracking-wider text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-md">
                                                    {tpl?.language || 'pt_BR'}
                                                </span>
                                                <span className="text-[10px] px-1.5 py-0.5 rounded-md font-medium bg-green-50 text-green-700">
                                                    {t('chat.templates.messenger.approved', 'Aprovado')}
                                                </span>
                                            </div>

                                            <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed">
                                                {textoDoBody(tpl?.components)}
                                            </p>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>

                    {/* Preview + variáveis */}
                    <div className="flex-1 flex flex-col bg-gradient-to-b from-slate-50 to-slate-100/50 overflow-hidden">
                        {!selecionado ? (
                            <div className="flex-1 flex flex-col items-center justify-center text-gray-400 p-8">
                                <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mb-4 shadow-sm border border-gray-100">
                                    <Search className="w-8 h-8 opacity-20" />
                                </div>
                                <p className="text-sm font-medium">{t('chat.templates.selectToPreview')}</p>
                            </div>
                        ) : (
                            <>
                                {/* Formulário e preview lado a lado: empilhados, o mockup do
                                    celular (~500px) empurrava os campos de variável para fora
                                    da área visível e o usuário não achava onde preencher. */}
                                <div className="flex-1 min-h-0 flex">
                                    <div className="flex-1 min-w-0 overflow-y-auto p-6 custom-scrollbar">
                                        <div className="max-w-md space-y-5">
                                            {semVariaveis && (
                                                <div className="bg-white rounded-xl border border-gray-200 p-4">
                                                    <p className="text-sm text-gray-500">
                                                        {t('chat.templates.messenger.noVariables', 'Este template não tem variáveis para preencher — é só enviar.')}
                                                    </p>
                                                </div>
                                            )}

                                            {(vars?.header?.length > 0 || vars?.body?.length > 0) && (
                                                <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
                                                    <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                                                        {t('chat.templates.messenger.variables', 'Variáveis')}
                                                    </p>
                                                    {(vars.header || []).map((ph) => campoVariavel(
                                                        `h-${ph}`,
                                                        t('chat.templates.messenger.headerLabel', 'Título'),
                                                        valores[chaveHeader(ph)] || '',
                                                        (v) => setValores((prev) => ({ ...prev, [chaveHeader(ph)]: v })),
                                                        `{{${ph}}}`
                                                    ))}
                                                    {(vars.body || []).map((ph) => campoVariavel(
                                                        `b-${ph}`,
                                                        t('chat.templates.messenger.bodyLabel', 'Mensagem'),
                                                        valores[chaveBody(ph)] || '',
                                                        (v) => setValores((prev) => ({ ...prev, [chaveBody(ph)]: v })),
                                                        `{{${ph}}}`
                                                    ))}
                                                </div>
                                            )}

                                            {vars?.buttons?.length > 0 && (
                                                <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
                                                    <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                                                        {t('chat.templates.buttonVariables')}
                                                    </p>
                                                    {vars.buttons.map((b) => campoVariavel(
                                                        `bt-${b.index}`,
                                                        `${b.text} — ${b.type === 'URL'
                                                            ? t('chat.templates.messenger.urlSuffix', 'sufixo da URL')
                                                            : t('chat.templates.messenger.payload', 'payload')}`,
                                                        valoresBotoes[b.index] || '',
                                                        (v) => setValoresBotoes((prev) => ({ ...prev, [b.index]: v }))
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Em telas estreitas o preview sai e o formulário fica com
                                        a largura toda — preencher a mensagem vem antes de ver
                                        como ela chega. */}
                                    <div className="hidden xl:block w-[348px] flex-shrink-0 border-l border-gray-100 overflow-y-auto p-6 custom-scrollbar">
                                        {/* Sem pageName: o objeto `chat` só tem o CONTATO
                                            (messengerUser), não a Página. O preview cai no
                                            rótulo padrão em vez de mostrar o nome errado. */}
                                        <MessengerPreview
                                            headerType={header ? (headerFormato === 'IMAGE' ? 'IMAGE' : 'TEXT') : 'none'}
                                            headerText={header?.text || ''}
                                            headerImageUrl={header?.mediaUrl || header?.example?.header_url || ''}
                                            body={textoDoBody(selecionado.components)}
                                            buttons={botoesDoTemplate(selecionado.components)}
                                            valores={mapaPorPlaceholder(vars?.body, valores, chaveBody)}
                                            valoresHeader={mapaPorPlaceholder(vars?.header, valores, chaveHeader)}
                                        />
                                    </div>
                                </div>

                                {/* Ações */}
                                <div className="p-4 border-t border-gray-100 bg-white/95 dark:bg-none backdrop-blur relative">
                                    {erroEnvio && (
                                        <div className="absolute bottom-full mb-3 left-1/2 -translate-x-1/2 bg-red-50 text-red-600 px-4 py-2 rounded-lg text-sm border border-red-100 shadow-sm animate-in fade-in slide-in-from-bottom-2 max-w-lg text-center">
                                            {erroEnvio}
                                        </div>
                                    )}

                                    <div className="flex items-center justify-between max-w-4xl mx-auto gap-4">
                                        <div className="text-sm text-gray-600">
                                            {faltaPreencher && (
                                                <span className="flex items-center gap-2">
                                                    <span className="w-2 h-2 rounded-full bg-[#7C3AED]" />
                                                    {t('chat.templates.fillVariablesHint')}
                                                </span>
                                            )}
                                        </div>

                                        <div className="flex items-center gap-3">
                                            <Button
                                                variant="ghost"
                                                onClick={() => setSelecionado(null)}
                                                className="text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                                            >
                                                {t('common.buttons.cancel')}
                                            </Button>

                                            <Button
                                                onClick={handleEnviar}
                                                disabled={faltaPreencher || enviando || !psid}
                                                className="h-10 px-6 rounded-xl font-medium shadow-sm transition-all text-white disabled:opacity-60"
                                                style={{ backgroundColor: BRAND_COLOR }}
                                            >
                                                {enviando ? (
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                                        <span>{t('chat.templates.sending')}</span>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center gap-2">
                                                        <Send className="w-4 h-4" />
                                                        <span>{t('chat.templates.sendMessage')}</span>
                                                    </div>
                                                )}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
